import React, { useCallback, useMemo, useState } from 'react';

import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { PopupEntryPoint } from '../shared/popup-entry-point';

import { AutoOpenPopupTrigger } from './AutoOpenPopupTrigger';
import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint, type EntryPointParams } from './premiumPopup.entrypoint';

export const PremiumPopup = (): React.JSX.Element => {
	const {
		cloudId,
		product,
		PremiumWrapper,
		trialDaysLeft,
		userRoles,
		shouldAutoOpen,
		onAutoOpen,
		isInTrial,
		isInReverseTrial,
	} = useEditionAwareness();

	const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);
	const handleClosePremiumModal = useCallback(() => setIsPremiumModalOpen(false), []);
	const handleOpenPremiumModal = useCallback(() => setIsPremiumModalOpen(true), []);

	const entryPointParams: EntryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
		}),
		[product, cloudId],
	);

	const entryPointProps = useMemo(
		() => ({ handleOpenPremiumModal, onClose: handleClosePremiumModal }),
		[handleOpenPremiumModal, handleClosePremiumModal],
	);

	const onClose = useCallback(() => {
		// This will reset the auto open state from product
		onAutoOpen?.();
	}, [onAutoOpen]);

	if (!PremiumWrapper) {
		// Shouldn't happen, but if it does then we can at least still show the customer a dialog
		return (
			<>
				{shouldAutoOpen ? (
					<AutoOpenPopupTrigger
						entryPoint={entryPoint}
						entryPointParams={entryPointParams}
						entryPointProps={entryPointProps}
						placement={'bottom-end'}
						onClose={onClose}
						dialogConfig={DIALOG_CONFIG}
					/>
				) : (
					<PopupEntryPoint
						entryPoint={entryPoint}
						entryPointParams={entryPointParams}
						entryPointProps={entryPointProps}
						placement={'bottom-end'}
						onClose={onClose}
						dialogConfig={DIALOG_CONFIG}
					/>
				)}
			</>
		);
	}

	return (
		<>
			<PremiumWrapper
				isInTrial={Boolean(isInTrial)}
				isInReverseTrial={Boolean(isInReverseTrial)}
				trialDaysLeft={trialDaysLeft}
				isSiteAdmin={!!userRoles?.includes('SITE_ADMIN')}
				isPremiumModalOpen={isPremiumModalOpen}
				onClosePremiumModal={handleClosePremiumModal}
			>
				{shouldAutoOpen ? (
					<AutoOpenPopupTrigger
						entryPoint={entryPoint}
						entryPointParams={entryPointParams}
						entryPointProps={entryPointProps}
						placement={'bottom-end'}
						onClose={onClose}
						dialogConfig={DIALOG_CONFIG}
					/>
				) : (
					<PopupEntryPoint
						entryPoint={entryPoint}
						entryPointParams={entryPointParams}
						entryPointProps={entryPointProps}
						placement={'bottom-end'}
						onClose={onClose}
						dialogConfig={DIALOG_CONFIG}
					/>
				)}
			</PremiumWrapper>
		</>
	);
};
